import { Modal, ModalHeader, ModalBody, Spinner } from 'reactstrap';
import { useState, useEffect, useContext, useCallback } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import { Context as AuthContext } from '../../../../../contexts/AuthContext';
import PerformanceDisciplinaryActionService from '../../../../../services/PerformanceDisciplinaryAction';
import DisciplinaryActionCard from './DisciplinaryActionCard';
import SummaryModalRecordRow from '../components/SummaryModalRecordRow';

interface DisciplinaryActionItem {
  id?: number;
  name?: string;
  date?: string;
  dateOfIncident?: string;
  position?: string;
  department?: string;
  supervisor?: string;
  date_of_hire?: string;
  dateOfHire?: string;
  policy_violation?: string;
  policyViolation?: { name: string };
  policyViolations?: { name?: string }[];
  policy_violated?: string;
  reason_for_disciplinary_action?: string;
  reasonForDisciplinaryAction?: string;
  action_taken?: string;
  actionTaken?: string;
  expected_improvement?: string;
  expectedImprovement?: string;
  private_notes?: string;
  privateNotes?: string;
  created_at?: string;
  createdAt?: string;
  data?: DisciplinaryActionItem;
}

interface DisciplinaryActionSummaryModalProps {
  isOpen: boolean;
  toggle: () => void;
  userId?: string | null;
  userName?: string;
  DisciplinaryActionData?: DisciplinaryActionItem[];
}

const getDisciplinaryListDescription = (item: DisciplinaryActionItem) => {
  if (item.policyViolations && Array.isArray(item.policyViolations)) {
    const joined = item.policyViolations.map((pv: any) => pv.name || pv).join(', ');
    if (joined) {
      return joined;
    }
  }
  return item.policy_violation ?? item.policyViolation?.name ?? item.policy_violated ?? '';
};

const DisciplinaryActionSummaryModal = ({
  isOpen,
  toggle,
  userId,
  userName,
  DisciplinaryActionData,
}: DisciplinaryActionSummaryModalProps) => {
  const [disciplinaryActions, setDisciplinaryActions] = useState<DisciplinaryActionItem[]>([]);
  const [selected, setSelected] = useState<DisciplinaryActionItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { token } = useContext(AuthContext);

  const formatDate = (date: string | undefined) => {
    if (!date) return '-';
    return moment.utc(date).format('MM/DD/YYYY');
  };

  const loadDisciplinaryActions = useCallback(async () => {
    if (DisciplinaryActionData && DisciplinaryActionData.length > 0) {
      const processedData = DisciplinaryActionData.map((item: any) => item.data || item);
      setDisciplinaryActions(processedData);
      return;
    }

    if (!userId || !token) {
      return;
    }

    setIsLoading(true);
    try {
      const service = PerformanceDisciplinaryActionService(token);
      const response = await service.getUserDisciplinaryActions(userId, 'disciplinary-action');
      if (response?.data) {
        setDisciplinaryActions(response.data);
      }
    } catch (error: any) {
      console.error('Error loading disciplinary actions:', error);
      toast.error(error?.response?.data?.message || 'Error loading disciplinary actions');
    } finally {
      setIsLoading(false);
    }
  }, [userId, token, DisciplinaryActionData]);

  useEffect(() => {
    if (isOpen) {
      setSelected(null);
      loadDisciplinaryActions();
    }
  }, [isOpen, loadDisciplinaryActions]);

  const handleClose = () => {
    setSelected(null);
    setDisciplinaryActions([]);
    toggle();
  };

  const renderDetailCard = (disciplinaryAction: DisciplinaryActionItem, index: number) => (
    <DisciplinaryActionCard
      key={disciplinaryAction.id || index}
      index={index}
      name={disciplinaryAction.name ?? ''}
      date={formatDate(disciplinaryAction.date || disciplinaryAction.dateOfIncident)}
      position={disciplinaryAction.position ?? ''}
      department={disciplinaryAction.department ?? ''}
      supervisor={disciplinaryAction.supervisor ?? ''}
      dateOfHire={formatDate(disciplinaryAction.date_of_hire ?? disciplinaryAction.dateOfHire)}
      policyViolation={
        disciplinaryAction.policyViolations && Array.isArray(disciplinaryAction.policyViolations)
          ? disciplinaryAction.policyViolations.map((pv: any) => pv.name || pv).join(', ')
          : disciplinaryAction.policy_violation ?? disciplinaryAction.policyViolation?.name ?? 'N/A'
      }
      reasonForDisciplinaryAction={
        disciplinaryAction.reason_for_disciplinary_action ?? disciplinaryAction.reasonForDisciplinaryAction ?? ''
      }
      actionTaken={disciplinaryAction.action_taken ?? disciplinaryAction.actionTaken ?? ''}
      expectedImprovement={disciplinaryAction.expected_improvement ?? disciplinaryAction.expectedImprovement ?? ''}
      privateNotes={disciplinaryAction.private_notes ?? disciplinaryAction.privateNotes}
      createdAt={formatDate(disciplinaryAction.created_at ?? disciplinaryAction.createdAt)}
    />
  );

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      {selected ? (
        <div className="modal-header align-items-start">
          <button
            type="button"
            className="btn btn-link p-0 mr-3 text-dark mt-1"
            onClick={() => setSelected(null)}
            aria-label="Back"
          >
            <i className="fas fa-arrow-left fa-lg" />
          </button>
          <div className="flex-grow-1">
            <h5 className="mb-0 modal-title">Disciplinary Action Notice</h5>
            {userName ? <div className="text-muted small">{userName}</div> : null}
          </div>
          <button type="button" className="close" onClick={handleClose} aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      ) : (
        <ModalHeader toggle={handleClose} tag="div">
          <div>
            <h5 className="mb-0 modal-title">Disciplinary Action Notice</h5>
            {userName ? <div className="text-muted small">{userName}</div> : null}
          </div>
        </ModalHeader>
      )}
      <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto', paddingTop: selected ? '1rem' : '0' }}>
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : selected ? (
          <div className="p-1">{renderDetailCard(selected, 0)}</div>
        ) : (
          <>
            {disciplinaryActions.length === 0 ? (
              <div className="text-center text-muted py-4">
                <i className="fas fa-inbox fa-3x mb-3" />
                <p>No disciplinary action records found</p>
              </div>
            ) : (
              <div className="pt-2">
                {disciplinaryActions.map((disciplinaryAction, index) => (
                  <SummaryModalRecordRow
                    key={disciplinaryAction.id || index}
                    dateText={formatDate(disciplinaryAction.date || disciplinaryAction.dateOfIncident)}
                    description={getDisciplinaryListDescription(disciplinaryAction) || '—'}
                    onClick={() => setSelected(disciplinaryAction)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default DisciplinaryActionSummaryModal;
